import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { EventEntityType, EventType } from '@kfolks/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { type AuditTimelineEntryDTO, requestCenterApi } from '@/services/request-center-api'

/**
 * Admin cross-request audit search page (Spec 3 task 16.3 – 16.6).
 *
 * Filters: actor (userId), action type (multi-select event-type), entity
 * type (defaults to `request_center_request`), date-range. Results render
 * as a table with one row per event; clicking a row expands a JSON dump
 * of the `data` payload. Pagination uses page (`limit` fixed at 50).
 *
 * Admin gating is enforced by the route layer (`<RequireRole>`); this
 * page assumes the caller is already an admin. The backend also gates
 * the endpoint and returns 403 for non-admins.
 */

const PAGE_SIZE = 50

interface FilterState {
	actor: string
	actionType: EventType[]
	entityType: EventEntityType
	from: string
	to: string
}

const blankFilters = (): FilterState => ({
	actor: '',
	actionType: [],
	entityType: EventEntityType.REQUEST_CENTER_REQUEST,
	from: '',
	to: '',
})

export function AuditSearchPage() {
	const [filters, setFilters] = useState<FilterState>(blankFilters())
	const [page, setPage] = useState(1)
	// `pendingFilters` is the live form state; `appliedFilters` is the snapshot
	// the query reads. Splitting them avoids re-querying on every keystroke;
	// the user clicks "Apply" to commit.
	const [appliedFilters, setAppliedFilters] = useState<FilterState>(blankFilters())

	const { data, isLoading, isError } = useQuery({
		queryKey: ['admin-audit-search', appliedFilters, page] as const,
		queryFn: () =>
			requestCenterApi.searchAudit({
				actor: appliedFilters.actor || undefined,
				actionType: appliedFilters.actionType.length > 0 ? appliedFilters.actionType : undefined,
				entityType: appliedFilters.entityType || undefined,
				from: appliedFilters.from || undefined,
				to: appliedFilters.to || undefined,
				limit: PAGE_SIZE,
				page,
			}),
	})

	const totalPages = useMemo(() => {
		const total = data?.total ?? 0
		return total === 0 ? 1 : Math.ceil(total / PAGE_SIZE)
	}, [data?.total])

	const onApply = () => {
		setAppliedFilters(filters)
		setPage(1)
	}
	const onReset = () => {
		const blank = blankFilters()
		setFilters(blank)
		setAppliedFilters(blank)
		setPage(1)
	}

	return (
		<div className="space-y-4">
			<header className="flex items-center justify-between">
				<h1 className="text-2xl font-semibold tracking-tight">Audit search</h1>
			</header>

			<FilterBar filters={filters} onChange={setFilters} onApply={onApply} onReset={onReset} />

			<ResultsTable rows={data?.data ?? []} isLoading={isLoading} isError={isError} />

			<Pagination
				page={page}
				totalPages={totalPages}
				total={data?.total ?? 0}
				onPrev={() => setPage((p) => Math.max(1, p - 1))}
				onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
			/>
		</div>
	)
}

// ─── Filter bar ──────────────────────────────────────────────────────────────

interface FilterBarProps {
	filters: FilterState
	onChange: (next: FilterState) => void
	onApply: () => void
	onReset: () => void
}

function FilterBar({ filters, onChange, onApply, onReset }: FilterBarProps) {
	const eventTypeOptions = Object.values(EventType)
	const entityTypeOptions = Object.values(EventEntityType)

	return (
		<div
			className="grid gap-3 rounded border border-input bg-muted/20 p-4 sm:grid-cols-2 lg:grid-cols-3"
			data-testid="audit-filter-bar"
		>
			<label className="space-y-1 text-xs">
				<span className="font-medium text-muted-foreground">Actor (userId)</span>
				<Input
					value={filters.actor}
					onChange={(e) => onChange({ ...filters, actor: e.target.value })}
					placeholder="employee/user UUID"
					data-testid="filter-actor"
				/>
			</label>
			<label className="space-y-1 text-xs">
				<span className="font-medium text-muted-foreground">Action type</span>
				<select
					multiple
					value={filters.actionType}
					onChange={(e) => {
						const selected = Array.from(e.target.selectedOptions).map((opt) => opt.value as EventType)
						onChange({ ...filters, actionType: selected })
					}}
					className="block h-24 w-full rounded border border-input bg-background px-2 py-1 text-sm"
					data-testid="filter-action-type"
				>
					{eventTypeOptions.map((type) => (
						<option key={type} value={type}>
							{type}
						</option>
					))}
				</select>
			</label>
			<label className="space-y-1 text-xs">
				<span className="font-medium text-muted-foreground">Entity type</span>
				<select
					value={filters.entityType}
					onChange={(e) => onChange({ ...filters, entityType: e.target.value as EventEntityType })}
					className="block w-full rounded border border-input bg-background px-2 py-2 text-sm"
					data-testid="filter-entity-type"
				>
					{entityTypeOptions.map((type) => (
						<option key={type} value={type}>
							{type}
						</option>
					))}
				</select>
			</label>
			<label className="space-y-1 text-xs">
				<span className="font-medium text-muted-foreground">From (ISO 8601)</span>
				<Input
					type="datetime-local"
					value={filters.from}
					onChange={(e) => onChange({ ...filters, from: e.target.value })}
					data-testid="filter-from"
				/>
			</label>
			<label className="space-y-1 text-xs">
				<span className="font-medium text-muted-foreground">To (ISO 8601)</span>
				<Input
					type="datetime-local"
					value={filters.to}
					onChange={(e) => onChange({ ...filters, to: e.target.value })}
					data-testid="filter-to"
				/>
			</label>
			<div className="flex items-end gap-2">
				<Button onClick={onApply} data-testid="filter-apply">
					Apply
				</Button>
				<Button variant="outline" onClick={onReset} data-testid="filter-reset">
					Reset
				</Button>
			</div>
		</div>
	)
}

// ─── Results table ───────────────────────────────────────────────────────────

interface ResultsTableProps {
	rows: AuditTimelineEntryDTO[]
	isLoading: boolean
	isError: boolean
}

function ResultsTable({ rows, isLoading, isError }: ResultsTableProps) {
	if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>
	if (isError) return <p className="text-sm text-muted-foreground">Failed to load audit results.</p>
	if (rows.length === 0) return <p className="text-sm text-muted-foreground">No matching events.</p>

	return (
		<Table data-testid="audit-results-table">
			<TableHeader>
				<TableRow>
					<TableHead>When</TableHead>
					<TableHead>Type</TableHead>
					<TableHead>Actor</TableHead>
					<TableHead>Details</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{rows.map((row) => (
					<ResultRow key={row.eventId} row={row} />
				))}
			</TableBody>
		</Table>
	)
}

function ResultRow({ row }: { row: AuditTimelineEntryDTO }) {
	const [expanded, setExpanded] = useState(false)
	const when = new Date(row.timestamp)
	const whenStr = Number.isNaN(when.getTime()) ? row.timestamp : when.toLocaleString()
	const actor = row.actorDisplayName ?? row.actorUserId ?? 'system'

	return (
		<>
			<TableRow data-testid="audit-result-row" data-event-id={row.eventId}>
				<TableCell className="whitespace-nowrap text-xs">{whenStr}</TableCell>
				<TableCell>
					<Badge variant="outline">{row.type}</Badge>
				</TableCell>
				<TableCell className="text-sm">{actor}</TableCell>
				<TableCell>
					<button
						type="button"
						onClick={() => setExpanded((prev) => !prev)}
						className="text-xs text-muted-foreground underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						aria-expanded={expanded}
						data-testid="audit-row-toggle"
					>
						{expanded ? 'Hide' : 'Show'} payload
					</button>
				</TableCell>
			</TableRow>
			{expanded ? (
				<TableRow data-testid="audit-row-detail">
					<TableCell colSpan={4} className="bg-muted/20">
						<pre className="overflow-x-auto whitespace-pre-wrap break-all text-xs">
							{JSON.stringify(row.data, null, 2)}
						</pre>
					</TableCell>
				</TableRow>
			) : null}
		</>
	)
}

// ─── Pagination ──────────────────────────────────────────────────────────────

interface PaginationProps {
	page: number
	totalPages: number
	total: number
	onPrev: () => void
	onNext: () => void
}

function Pagination({ page, totalPages, total, onPrev, onNext }: PaginationProps) {
	if (total === 0) return null
	return (
		<div className="flex items-center justify-between gap-2 text-sm" data-testid="audit-pagination">
			<span className="text-muted-foreground">
				Page {page} of {totalPages} · {total} total
			</span>
			<div className="flex gap-2">
				<Button variant="outline" onClick={onPrev} disabled={page <= 1} data-testid="page-prev">
					Previous
				</Button>
				<Button variant="outline" onClick={onNext} disabled={page >= totalPages} data-testid="page-next">
					Next
				</Button>
			</div>
		</div>
	)
}
